import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { FileFormatApiRow } from '@/api/file-formats';
import { AddFileFormatModal } from '@/components/file-formats/add-file-format-modal';
import { DeleteFileFormatDialog } from '@/components/file-formats/delete-file-format-dialog';
import { EditFileFormatModal } from '@/components/file-formats/edit-file-format-modal';
import { FileFormatsTable } from '@/components/file-formats/file-formats-table';
import { RestoreFileFormatDialog } from '@/components/file-formats/restore-file-format-dialog';
import {
  type FileFormatScopeFilter,
  type FileFormatStatusFilterUi,
  useFileFormats,
} from '@/hooks/use-file-formats';

export const FileFormatsPage: React.FC = () => {
  const {
    tableData,
    isLoading,
    error,
    filters,
    scopeUi,
    statusUi,
    setSearch,
    setScope,
    setStatus,
    setPage,
    setSort,
    refetch,
  } = useFileFormats();
  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState<FileFormatApiRow | null>(null);
  const [deleteRow, setDeleteRow] = useState<FileFormatApiRow | null>(null);
  const [restoreRow, setRestoreRow] = useState<FileFormatApiRow | null>(null);

  const getRowLink = useCallback(
    (row: FileFormatApiRow) => ({ to: `/file-formats/${row.id}` }),
    []
  );

  // DataTable's scope dropdown auto-injects an "All" item that sends `''`,
  // mapped here to the hook's 'all' state (which omits `scope` from the
  // backend query). The status dropdown has `hideAllOption: true`, so it
  // only ever sends 'active' or 'deleted' verbatim and never needs the
  // empty-string -> 'all' translation.
  const handleFilterChange = useCallback(
    (filterKey: string, value: string | string[]) => {
      const raw = Array.isArray(value) ? (value[0] ?? '') : value;
      if (filterKey === 'scope') {
        setScope((raw === '' ? 'all' : raw) as FileFormatScopeFilter);
      } else if (filterKey === 'status') {
        setStatus(raw as FileFormatStatusFilterUi);
      }
    },
    [setScope, setStatus]
  );

  // Inverse of handleFilterChange: scope's 'all' UI state renders as the
  // auto-injected "All" item (controlled via the empty string the Select
  // uses internally); status passes through verbatim because its dropdown
  // has no "All" item to round-trip into.
  const currentFilterValues = useMemo<Record<string, string>>(
    () => ({
      scope: scopeUi === 'all' ? '' : scopeUi,
      status: statusUi,
    }),
    [scopeUi, statusUi]
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">File Formats</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Define how the platform parses and generates payment files — system-wide or scoped to a
          provider or client.
        </p>
      </div>

      <FileFormatsTable
        data={tableData}
        isLoading={isLoading}
        error={error}
        currentFilterValues={currentFilterValues}
        currentSortKey={filters.sortBy ?? 'format_name'}
        currentSortOrder={filters.order ?? 'asc'}
        searchValue={filters.search ?? ''}
        onFilterChange={handleFilterChange}
        onSortChange={setSort}
        onPageChange={setPage}
        onSearchChange={setSearch}
        onAdd={() => setAddOpen(true)}
        onEdit={(row) => setEditRow(row)}
        onDelete={(row) => setDeleteRow(row)}
        onRestore={(row) => setRestoreRow(row)}
        getRowLink={getRowLink}
      />

      <AddFileFormatModal open={addOpen} onOpenChange={setAddOpen} onSuccess={refetch} />
      <EditFileFormatModal
        open={editRow !== null}
        row={editRow}
        onOpenChange={(open) => {
          if (!open) setEditRow(null);
        }}
        onSuccess={refetch}
      />
      <DeleteFileFormatDialog
        open={deleteRow !== null}
        row={deleteRow}
        onOpenChange={(open) => {
          if (!open) setDeleteRow(null);
        }}
        onSuccess={refetch}
      />
      <RestoreFileFormatDialog
        open={restoreRow !== null}
        row={restoreRow}
        onOpenChange={(open) => {
          if (!open) setRestoreRow(null);
        }}
        onSuccess={refetch}
      />
    </div>
  );
};
